import { Epic, ofType } from "redux-observable";
import { of, from, concat } from "rxjs";
import { map, catchError, switchMap, withLatestFrom, take, mergeMap } from "rxjs/operators";
import type { RootState } from "@/store";
import { trashPageActions } from "./dataLoaderSlice";
import { deleteFileActions } from "@/store/deleteFile/deleteFileSlice";
import { FolderService } from "@/services/FolderService";
import { fileActions } from "../file";
import HttpClient from "@/api/http-client";
import { handleHttpError } from "@/api/HttpClient";
import { message } from "antd";

/**
 * Epic xử lý getDataRequest
 * 
 * Logic:
 * - MY_DRIVE: gọi deleteFileActions.getDeletedFilesRequest (không có folderId)
 * - TEAM_DRIVE: gọi FolderService với isDelete: true
 * - OPEN_FOLDER: gọi FolderService không có isDelete
 */
export const getDataRequestEpic: Epic<any, any, RootState> = (
    action$,
    state$
) =>
    action$.pipe(
        ofType(trashPageActions.getDataRequest.type),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const { source, pagination } = state.trashPage;
            const { page, pageSize } = pagination;
            const currentFolderId = state.trashPage.trashFromFolderId;


            // ✅ CASE 1: MY_DRIVE - Trash của My Drive
            if (source === "MY_DRIVE") {
                const folderRootId = localStorage.getItem("folderRootId");
                
                if (!folderRootId) {
                    return of(
                        trashPageActions.getDataFailure("Folder Root ID not found")
                    );
                }

                return from(
                    FolderService.Get.getItemsDeletedFromMyDrive(folderRootId, {
                        page,
                        pageSize,
                        isDeleted: true, 
                    }).toPromise()
                ).pipe(
                    mergeMap((response: any) => {
                        const files = response?.data?.results || response?.results || [];
                        const totalCount = response?.data?.queryCount || response?.queryCount || files.length;

                        return [
                            trashPageActions.getDataSuccess({
                                trashSource: {
                                    id: "my-drive",
                                    name: "My Drive",
                                    type: "my-drive",
                                },
                                data: files,
                                totalCount,
                            }),
                            fileActions.setPagination({
                                page: response?.data?.page || page,
                                pageCount: response?.data?.pageCount,
                                pageSize: response?.data?.pageSize,
                                queryCount: response?.data?.queryCount,
                                firstRowIndex: (response?.data?.firstRowIndex),
                                lastRowIndex: (response?.data?.lastRowIndex),
                            }),
                        ];
                    }),
                    catchError((error) => {
                        message.error(handleHttpError(error));

                        return of(
                            trashPageActions.getDataFailure(
                                error?.message || "Error fetching My Drive trash data"
                            )
                        );
                    })
                );
            }

            // ✅ CASE 2: TEAM_DRIVE - Trash của Team Drive
            if (source === "TEAM_DRIVE") {
                if (!currentFolderId) {
                    return of(
                        trashPageActions.getDataFailure(
                            "Folder ID is required for TEAM_DRIVE"
                        )
                    );
                }

                return from(
                    FolderService.Get.getFilesByFolderId(currentFolderId, {
                        page,
                        pageSize,
                        isDeleted: true, 
                    }).toPromise()
                ).pipe(
                    mergeMap((response: any) => {
                        const files = response?.data?.results || response?.results || [];
                        const totalCount = response?.data?.queryCount || response?.queryCount || files.length;

                        return [
                            trashPageActions.getDataSuccess({
                                trashSource: {
                                    id: currentFolderId,
                                    name: "Team Drive Trash",
                                    type: "team-drive",
                                },
                                data: files,
                                totalCount,
                            }),
                            fileActions.setPagination({
                                page: response?.data?.page || page,
                                pageCount: response?.data?.pageCount,
                                pageSize: response?.data?.pageSize,
                                queryCount: response?.data?.queryCount,
                                firstRowIndex: (response?.data?.firstRowIndex),
                                lastRowIndex: (response?.data?.lastRowIndex),
                            }),
                        ];
                    }),
                    catchError((error) => {
                        message.error(handleHttpError(error));

                        return of(
                            trashPageActions.getDataFailure(
                                error?.message || "Error fetching folder data"
                            )
                        );
                    })
                );

            }

            // ✅ CASE 3: OPEN_FOLDER - Mở folder bình thường
            if (source === "OPEN_FOLDER") {
                const FolderId = localStorage.getItem("folderId");
                if (!FolderId) {
                    return of(
                        trashPageActions.getDataFailure(
                            "Folder ID is required for OPEN_FOLDER"
                        )
                    );
                }

                return from(
                    FolderService.Get.getFilesByFolderId(FolderId, {
                        page,
                        pageSize,
                        // ✅ KHÔNG truyền isDelete
                    }).toPromise()
                ).pipe(
                    mergeMap((response: any) => {
                        const files = response?.data?.results || response?.results || [];
                        const totalCount = response?.data?.queryCount || response?.queryCount || files.length;

                        return [
                            trashPageActions.getDataSuccess({
                                trashSource: {
                                    id: currentFolderId,
                                    name: "Team Drive Trash",
                                    type: "team-drive",
                                },
                                data: files,
                                totalCount,
                            }),
                            fileActions.setParentFiles(files),

                            fileActions.setPagination({
                                page: response?.data?.page || page,
                                pageCount: response?.data?.pageCount,
                                pageSize: response?.data?.pageSize,
                                queryCount: response?.data?.queryCount,
                                firstRowIndex: (response?.data?.firstRowIndex),
                                lastRowIndex: (response?.data?.lastRowIndex),
                            }),
                        ];
                    }),
                    catchError((error) => {
                        message.error(handleHttpError(error));

                        return of(
                            trashPageActions.getDataFailure(
                                error?.message || "Error fetching folder data"
                            )
                        );
                    })
                );
            }

            // Fallback
            return of(
                trashPageActions.getDataFailure("Invalid data source")
            );
        })
    );

export const dataLoaderEpics = [getDataRequestEpic];
